/** @file Factory for `<domain>.create` commands: validate, mint or accept an id, check the name, insert. */
import type { z } from 'zod';
import { ids } from '@pose2d/format/ids';
import { ENTITY_KINDS } from '@pose2d/format/doc/entityKinds';
import { throwError } from '@pose2d/format/throwError';
import type { Entity, EntityKind, Id } from '@pose2d/format/types';
import { defineCommand, type CommandDef } from '../defineCommand';
import type { CommandContext } from '../commandContext';
import { KIND_PREFIX } from './kindPrefix';
import { assertFreeName } from './assertFreeName';

/** `build` returns the entity record of `kind`; the factory accepts `input.id` when unused (`ID_TAKEN` otherwise), mints one when absent, checks the name, and inserts. */
export function defineCreate<K extends EntityKind, I extends { id?: string; name: string }>(spec: { domain: string; kind: K; schema: z.ZodType<I>; description: string; build(input: I, ctx: CommandContext): Entity<K> }): CommandDef<I> {
  const prefix = KIND_PREFIX[spec.kind];
  return defineCommand({
    name: `${spec.domain}.create`,
    description: spec.description,
    input: spec.schema,
    apply(draft, input, ctx) {
      const id = input.id ?? ctx.newId(prefix);
      if (!ids.re(prefix).test(id)) throwError('VALIDATION', undefined, { path: 'id', hint: `expected a ${prefix}_ id` });
      if (ctx.index.kindOf.has(id)) throwError('ID_TAKEN', { id }, { path: 'id' });
      const entity = spec.build(input, ctx);
      assertFreeName(ctx, spec.kind, entity.name);
      (draft[ENTITY_KINDS[spec.kind]] as Record<Id, unknown>)[id] = entity;
      ctx.created[entity.name] = id;
      draft.meta.updatedAt = ctx.now;
    },
  });
}
